/**
 * What a search sends, shared by every page that asks the service a question.
 *
 * Two rules, both easy to get subtly wrong and neither visible by looking at
 * the page. They were written for the decisions search and are here because
 * the message log needs the same two, and a rule stated twice is the same rule
 * only until somebody edits one copy.
 */

/**
 * A wall clock from a date picker, as the instant the operator meant.
 *
 * `<input type="datetime-local">` has no offset by design: it is a wall clock,
 * and only the browser knows which one. Sent as it stands, the service has to
 * guess, and what it used to guess was the timezone of the container it runs
 * in. Measured on PostgreSQL on 2026-09-16: one string, "2026-09-15T12:00",
 * selected three different sets of rows at TZ=UTC, Europe/Berlin and
 * Pacific/Auckland, and the container runs at UTC, so an operator in Berlin
 * asking for the last day silently lost the first two hours of it.
 *
 * The service now reads a value with no offset as UTC, which makes the answer
 * the same everywhere. That is not the same as making it right: right is the
 * operator's own zone, and this is the only place that knows it.
 *
 * A value the browser cannot parse is passed through untouched rather than
 * dropped. A filter that vanishes on its way to the query leaves the console
 * showing a range it is not applying, which is the defect this whole change
 * exists to remove.
 */
export function asInstant(local: string | undefined): string | undefined {
  if (!local) return undefined
  const moment = new Date(local)
  return Number.isNaN(moment.getTime()) ? local : moment.toISOString()
}

/**
 * The inverse, for a page opened at an address another page produced.
 *
 * `asInstant` turns the operator's wall clock into an instant to send; this
 * turns an instant back into the wall clock a `datetime-local` control can
 * show, in the reader's own zone. Arriving from Attempts with a UTC range and
 * rendering it as UTC in the boxes would show a different two hours than the
 * list it just came from, in the same console, which is the kind of
 * disagreement nobody reports because it looks like a different range.
 *
 * An unparseable value yields an empty control rather than an invented one.
 */
export function fromInstant(instant: string | null | undefined): string {
  if (!instant) return ''
  const moment = new Date(instant)
  if (Number.isNaN(moment.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}` +
    `T${pad(moment.getHours())}:${pad(moment.getMinutes())}`
  )
}

/**
 * The query string for a search, carrying only what was asked.
 *
 * An empty filter is absent rather than sent as an empty string. The service
 * validates `outcome`, `basis` and `direction`, so a blank one is refused, and
 * the page would report an error for a filter nobody set.
 */
export function searchQuery(
  values: Record<string, string | null | undefined>,
  cursor?: string | null,
): string {
  const params = new URLSearchParams()
  for (const [name, value] of Object.entries(values)) {
    if (value) params.set(name, value)
  }
  if (cursor) params.set('cursor', cursor)
  return params.size ? `?${params}` : ''
}
